"use client";

import React from "react";
import { useTranslation } from "react-i18next";
import "./UserAverageRating.css";

const UserAverageRating = ({ averageRating, ratingsCount, ratingLabel: backendLabel, size = "medium" }) => {
  const { t } = useTranslation();

  if (averageRating === null || averageRating === undefined) {
    return null; // Don't show if rating is invalid (null/undefined)
  }

  const roundedRating = Math.round(averageRating * 2) / 2; // Round to nearest 0.5
  const fullStars = Math.floor(roundedRating);
  const hasHalfStar = roundedRating % 1 !== 0;
  const emptyStars = 5 - fullStars - (hasHalfStar ? 1 : 0);

  const starSize = size === "small" ? 14 : size === "large" ? 18 : 16;

  // Get rating label - use backend label if provided, otherwise calculate on frontend (backwards compatible)
  // If backendLabel is explicitly null, don't show label at all
  const getRatingLabel = (rating) => {
    // If backendLabel is explicitly null, return null to hide label
    if (backendLabel === null) {
      return null;
    }

    // If backend provides label, use it
    if (backendLabel) {
      return backendLabel;
    }

    // Fallback: calculate label on frontend (for backwards compatibility)
    if (rating === 0) {
      return ""; // No label for 0 rating
    } else if (rating > 0 && rating <= 1) {
      return t("userRating.labels.poor", "Poor");
    } else if (rating >= 1.1 && rating <= 2) {
      return t("userRating.labels.bad", "Bad");
    } else if (rating >= 2.1 && rating <= 3) {
      return t("userRating.labels.decent", "Decent");
    } else if (rating >= 3.1 && rating <= 4) {
      return t("userRating.labels.good", "Good");
    } else if (rating >= 4.1 && rating <= 5) {
      return t("userRating.labels.excellent", "Excellent");
    }
    return "";
  };

  const ratingLabel = getRatingLabel(roundedRating);

  return (
    <div className={`user-average-rating user-average-rating-${size}`}>
      <div className="user-average-rating-stars">
        {[...Array(fullStars)].map((_, i) => (
          <svg
            key={`full-${i}`}
            width={starSize}
            height={starSize}
            viewBox="0 0 24 24"
            fill="none"
            xmlns="http://www.w3.org/2000/svg"
          >
            <path
              d="M12 2L15.09 8.26L22 9.27L17 14.14L18.18 21.02L12 17.77L5.82 21.02L7 14.14L2 9.27L8.91 8.26L12 2Z"
              fill="#FBBF24"
              stroke="#FBBF24"
              strokeWidth="1.5"
            />
          </svg>
        ))}
        {hasHalfStar && (
          <svg
            width={starSize}
            height={starSize}
            viewBox="0 0 24 24"
            fill="none"
            xmlns="http://www.w3.org/2000/svg"
          >
            <defs>
              <clipPath id={`half-star-clip-${roundedRating}`}>
                <rect x="0" y="0" width="12" height="24" />
              </clipPath>
            </defs>
            <path
              d="M12 2L15.09 8.26L22 9.27L17 14.14L18.18 21.02L12 17.77L5.82 21.02L7 14.14L2 9.27L8.91 8.26L12 2Z"
              fill="none"
              stroke="#D1D5DB"
              strokeWidth="1.5"
            />
            <path
              d="M12 2L15.09 8.26L22 9.27L17 14.14L18.18 21.02L12 17.77L5.82 21.02L7 14.14L2 9.27L8.91 8.26L12 2Z"
              fill="#FBBF24"
              clipPath={`url(#half-star-clip-${roundedRating})`}
            />
          </svg>
        )}
        {[...Array(emptyStars)].map((_, i) => (
          <svg
            key={`empty-${i}`}
            width={starSize}
            height={starSize}
            viewBox="0 0 24 24"
            fill="none"
            xmlns="http://www.w3.org/2000/svg"
          >
            <path
              d="M12 2L15.09 8.26L22 9.27L17 14.14L18.18 21.02L12 17.77L5.82 21.02L7 14.14L2 9.27L8.91 8.26L12 2Z"
              fill="none"
              stroke="#D1D5DB"
              strokeWidth="1.5"
            />
          </svg>
        ))}
      </div>
      <span className="user-average-rating-value">
        {roundedRating.toFixed(1)}
      </span>
      {ratingLabel && (
        <span className="user-average-rating-label">
          ({ratingLabel})
        </span>
      )}
    </div>
  );
};

export default UserAverageRating;
